import { DraftState, DraftTextOrPlaceholderTextConfiguration, PrintArea, StoreFont } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { findCenterMostSpaceIndex } from '@/utils/text/findCenterMostSpaceIndex';
import { getTextSizeForStyle } from '@/utils/text/getTextSizeForStyle';
import { getRandomCid } from '@/model/configuration/getRandomCid';
import { isDraftTextOrPlaceHolderTextConfiguration } from '@/model/configuration/isDraftConfigurationType';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getPrintAreaForNewConfiguration } from '@/model/draft/getPrintAreaForNewConfiguration';
import { getPrintTypeForFontOnDraft } from '@/model/draft/getPrintTypeForFontOnDraft';
import { getProductType } from '@/model/draft/getProductType';
import { getPrintAreaWidth } from '@/model/printArea/getPrintAreaSize';
import { convertColors } from '@/model/printType/convertColors';
import { fitsPrintAreaAndPrintType } from '@/model/printType/fitsPrintAreaAndPrintType';
import { adjustTextStyleOffset } from './adjustTextStyleOffset';
import { getDefaultBox } from './getDefaultBox';
import { getOffset } from './getOffset';
import { getTextStyleForWidth } from './getTextStyleForWidth';

const hasTextDuplicate = (
  textConfigurations: DraftTextOrPlaceholderTextConfiguration[],
  font: StoreFont,
  text: string,
  printArea: PrintArea,
  offsetX: number,
  offsetY: number
) =>
  textConfigurations.some(
    ({
      text: {
        content,
        style: {
          font: { id: fontId },
        },
      },
      printArea: {
        id: printAreaId,
        boundary: {
          size: { width, height },
        },
      },
      offset: { x, y },
    }) => {
      return (
        fontId === font.id &&
        content === text &&
        printAreaId === printArea.id &&
        // offset should differ at least 2%
        Math.abs(x - offsetX) < width / 50 &&
        Math.abs(y - offsetY) < height / 50
      );
    }
  );

export const getTextConfigurationBase = (
  draft: DraftState,
  text: string,
  font: StoreFont,
  color: string
) => {
  const printArea = assertDefined(getPrintAreaForNewConfiguration(draft));
  const printType = getPrintTypeForFontOnDraft(draft, font);
  const defaultBox = getDefaultBox(printArea, printType, getProductType(draft));

  if (!printType) {
    return null;
  }

  let textStyle = getTextStyleForWidth(text, defaultBox.width, font, printType);
  let size = getTextSizeForStyle(textStyle, text);

  if (!fitsPrintAreaAndPrintType(size, printArea, printType)) {
    const centerMostSpaceIndex = findCenterMostSpaceIndex(text);
    if (centerMostSpaceIndex !== -1) {
      const twoLineText =
        text.substring(0, centerMostSpaceIndex) + '\n' + text.substr(centerMostSpaceIndex + 1);
      // create a copy because we only want to change the style if the two line version fits
      const twoLineTextStyle = { ...textStyle };
      adjustTextStyleOffset(twoLineTextStyle, twoLineText);
      const twoLineSize = getTextSizeForStyle(twoLineTextStyle, twoLineText);
      if (fitsPrintAreaAndPrintType(twoLineSize, printArea, printType)) {
        text = twoLineText;
        size = twoLineSize;
        textStyle = twoLineTextStyle;
      }
    }
  }

  const centerX = getPrintAreaWidth(printArea) / 2;
  let offsetX = centerX - size.width / 2;
  let offsetY = defaultBox.y;

  const textConfigurations = Object.values(getConfigurations(draft)).filter(
    isDraftTextOrPlaceHolderTextConfiguration
  );
  while (hasTextDuplicate(textConfigurations, font, text, printArea, offsetX, offsetY)) {
    const [shiftOffsetX, shiftOffsetY] = getOffset(printArea);
    offsetX += shiftOffsetX;
    offsetY += shiftOffsetY;
  }

  return {
    cid: getRandomCid(),
    printArea,
    printType,
    printColors: convertColors(printType, [{ fill: color }]),
    rotation: 0,
    offset: {
      x: offsetX,
      y: offsetY,
    },
    scale: 1,
    text: {
      content: text,
      style: textStyle,
    },
    size,
  };
};
